import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import axios from 'axios';
import { useLayout } from '../layout/context';
import { TwoThirds } from '../styled/containers';
import { StyledButton } from '../webchat/button/styles';
import { InlineCalloutAssembly } from '../inline-callout';
import InlineCallOut from '../inline-callout/inline-callout';
import { useBankHolidays } from '../webchat/use-bankholidays';
import { isBankHoliday } from '../webchat';
// eslint-disable-next-line import/no-relative-packages
import { WEBCHAT_STATUS_ENDPOINT } from '../../../plugins/gatsby-plugin-webchat/constants';

const statuses = {
  open: 'Online',
  closed: 'Offline',
  atCapacity: 'Busy',
  loading: 'Loading',
  bankHoliday: 'BankHoliday',
};

const checkWebChatStatus = async () => {
  try {
    const response = await axios.post(
      `${WEBCHAT_STATUS_ENDPOINT}`,
      {
        url: window.location.href,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      `Error fetching webchat status: ${error.response?.status} ${
        error.response?.statusText || error.message
      }`
    );
  }
};

const getStatus = (apiResponse, isHoliday, isLoading) => {
  if (isHoliday) {
    return statuses.bankHoliday;
  }

  if (isLoading) {
    return statuses.loading;
  }

  if (apiResponse?.availabilityReason) {
    return statuses[apiResponse.availabilityReason];
  }

  return statuses.closed;
};

const WebchatCta = ({ contentfulData }) => {
  const { webchat: webchatConfig } = contentfulData;

  const bankHolidays = useBankHolidays();
  const isHoliday = isBankHoliday(new Date(), bankHolidays);
  const [shouldPoll, setShouldPoll] = useState(true);

  const [cachedData, setCachedData] = useState(null);

  const {
    data: apiResponse,
    error,
    isLoading,
  } = useSWR(
    isHoliday || !shouldPoll ? null : webchatConfig.webchatService,
    checkWebChatStatus,
    {
      refreshInterval: 15000,
      revalidateOnFocus: true,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (retryCount >= 10) return;
        setTimeout(() => revalidate({ retryCount }), 5000);
      },
    }
  );

  useEffect(() => {
    if (apiResponse) {
      setCachedData(apiResponse);
    }
  }, [apiResponse]);

  const responseToUse = shouldPoll ? apiResponse : cachedData;

  const { insideSideBar } = useLayout();

  const status = getStatus(responseToUse, isHoliday, isLoading);
  const isOnline = status === statuses.open;

  const getCtaText = () => {
    if (status === statuses.bankHoliday) {
      return webchatConfig.webchatCtaOffline;
    }

    if (status === statuses.loading) {
      return 'Loading';
    }

    if (webchatConfig[`webchatCta${status}`]) {
      return webchatConfig[`webchatCta${status}`];
    }
  };

  // Open the webchat and stop polling the status end point
  const handleButtonClick = () => {
    document.getElementById('chatButton').click();
    setShouldPoll((prevState) => !prevState);
  };

  if (error) {
    return (
      <InlineCallOut bannerColor="white">
        Webchat is unavailable due to an error, please try again
      </InlineCallOut>
    );
  }

  return (
    <TwoThirds>
      {!isOnline &&
      status !== statuses.loading &&
      webchatConfig[`webchatCtaMessaging${status}`] ? (
        <InlineCalloutAssembly
          contentfulData={webchatConfig[`webchatCtaMessaging${status}`]}
        />
      ) : null}

      <StyledButton
        icon="speech-bubble"
        bg="secondary-standard"
        disabled={!isOnline}
        onClick={handleButtonClick}
        iconPlacement="left"
        size="xxl"
        fullWidth={insideSideBar}
        $insideSideBar={insideSideBar}
        $status={status}
      >
        {getCtaText()}
      </StyledButton>
    </TwoThirds>
  );
};

WebchatCta.propTypes = {
  contentfulData: PropTypes.object,
};

export default WebchatCta;
